import { useEffect, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { ChevronRight, X } from "lucide-react";

export default function Sidebar({ open, onClose }) {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");
    const location = useLocation();

    const fetchGroups = useCallback(async () => {
        try {
            const { data } = await api.get("/categories");
            setGroups(data);
        } catch (e) {
            console.error("Failed to load categories", e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchGroups();
    }, [fetchGroups]);

    const visibleGroups = filter
        ? groups
              .map((g) => ({
                  ...g,
                  pages: g.pages.filter(
                      (p) =>
                          p.title.toLowerCase().includes(filter.toLowerCase()) ||
                          p.category.toLowerCase().includes(filter.toLowerCase()),
                  ),
              }))
              .filter((g) => g.pages.length > 0)
        : groups;

    return (
        <>
            {open && (
                <div
                    className="fixed inset-0 bg-medical-900/40 z-30 md:hidden"
                    onClick={onClose}
                    data-testid="sidebar-overlay"
                />
            )}
            <aside
                data-testid="sidebar"
                className={`fixed md:static top-0 left-0 z-40 h-full w-80 flex-shrink-0 border-r border-slate-200 bg-white flex flex-col transform transition-transform duration-200 ${
                    open ? "translate-x-0" : "-translate-x-full"
                } md:translate-x-0`}
            >
                <div className="h-16 flex-shrink-0 border-b border-slate-200 px-5 flex items-center justify-between">
                    <Link to="/" className="block" onClick={onClose}>
                        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-teal-700">
                            ED Whiteboard
                        </div>
                        <div className="font-display text-base font-bold text-medical-900 leading-tight">
                            Table of Contents
                        </div>
                    </Link>
                    <button
                        className="md:hidden p-2 rounded hover:bg-slate-100"
                        onClick={onClose}
                        data-testid="sidebar-close-button"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="px-4 py-3 border-b border-slate-100">
                    <input
                        data-testid="sidebar-filter-input"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        placeholder="Filter contents…"
                        className="w-full h-9 px-3 text-sm rounded-md border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-600"
                    />
                </div>

                <nav className="flex-1 overflow-y-auto scroll-thin" data-testid="sidebar-nav">
                    {loading && (
                        <div className="p-6 text-sm text-slate-500">Loading contents…</div>
                    )}
                    {!loading && visibleGroups.length === 0 && (
                        <div className="p-6 text-sm text-slate-500">No matches.</div>
                    )}
                    {visibleGroups.map((g) => (
                        <div key={g.category} className="pb-2" data-testid={`sidebar-category-${g.category}`}>
                            <div className="sticky top-0 bg-white/95 backdrop-blur px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 border-b border-slate-100 z-10">
                                {g.category}{" "}
                                <span className="text-slate-300 font-normal">· {g.count}</span>
                            </div>
                            <ul>
                                {g.pages.map((p) => {
                                    const active = location.pathname === `/p/${p.id}`;
                                    return (
                                        <li key={p.id}>
                                            <Link
                                                to={`/p/${p.id}`}
                                                onClick={onClose}
                                                data-testid={`toc-link-${p.id}`}
                                                className={`group flex items-start gap-2 px-4 py-2.5 text-sm border-l-2 transition-colors ${
                                                    active
                                                        ? "bg-teal-50 text-teal-800 border-teal-700 font-medium"
                                                        : "text-slate-700 border-transparent hover:bg-slate-50 hover:border-teal-700 hover:text-medical-900"
                                                }`}
                                            >
                                                <span className="mt-0.5 font-mono text-[10px] text-slate-400 w-7 flex-shrink-0">
                                                    {String(p.page_number).padStart(3, "0")}
                                                </span>
                                                <span className="flex-1 leading-snug">{p.title}</span>
                                                <ChevronRight
                                                    className={`h-3.5 w-3.5 flex-shrink-0 mt-1 ${
                                                        active
                                                            ? "text-teal-700"
                                                            : "text-slate-300 group-hover:text-teal-700"
                                                    }`}
                                                />
                                            </Link>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    ))}
                </nav>
            </aside>
        </>
    );
}
